import type { FormalityRequestType } from "@/types/entities/formality-request";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import { VeredictCancelRequestModal } from "../cancelation/veredict-cancel-modal";
import { useMemo, useState } from "react";
import { useGetMe } from "@/queries/me";

interface CancelationWarningProps {
  formalityCode: string;
  cancellation_requests: FormalityRequestType["cancellation_requests"];
  requestGroups?: FormalityRequestType["groups"];
}

export const CancelationWarning = ({
  cancellation_requests,
  formalityCode,
  requestGroups,
}: CancelationWarningProps) => {
  const [open, setOpen] = useState(false);
  const { data: me } = useGetMe();

  const { latestCancellation, isCanceled, isDenied } = useMemo(() => {
    if (!cancellation_requests || cancellation_requests.length === 0) {
      return { latestCancellation: null, isCanceled: false, isDenied: false };
    }
    const latest = cancellation_requests[cancellation_requests.length - 1];
    return {
      latestCancellation: latest,
      isCanceled: latest?.approved_at !== null,
      isDenied: latest?.denied_at !== null,
    };
  }, [cancellation_requests]);
  const canUserApprove = useMemo(() => {
    const userSeniorGroups = me?.data.groups?.filter(
      (group) => group.pivot?.seniority === 1,
    );
    return requestGroups?.some((group) =>
      userSeniorGroups?.some((seniorGroup) => seniorGroup.id === group.id),
    );
  }, [me?.data.groups, requestGroups]);

  if (!latestCancellation || isDenied) {
    return null;
  }

  const { reason, requested_at, created_by } = latestCancellation;

  return (
    <>
      <div className="bg-neutral-object-accent-silent p-2 border border-neutral-quiet flex flex-col gap-1 rounded-lg">
        <Badge
          size="sm"
          variant={isCanceled ? "danger" : "warning"}
          className="shrink-0 grow-0 w-fit"
        >
          {isCanceled ? "Trámite cancelado" : "Solicitud de cancelación"}
        </Badge>
        <p className="text-body leading-3xl">{reason || ""}</p>
        <div>
          {requested_at && (
            <span className="text-body-quiet text-size-body-sm text-size-mono-xs">
              {dayjs(requested_at).isValid()
                ? dayjs(requested_at).format("DD [de] MMMM, YYYY")
                : ""}
            </span>
          )}{" "}
          {created_by && (
            <>
              <span className="text-size-body-sm text-body-quiet">por</span>{" "}
              <span className="text-size-body-sm text-action">
                {created_by.first_name || ""} {created_by.last_name || ""}
              </span>
            </>
          )}
        </div>
        {!isCanceled && canUserApprove && (
          <Button
            variant="tertiary"
            size="xs"
            className="shrink-0 gap-1 grow-0 w-fit text-warning"
            onClick={() => {
              setOpen(true);
            }}
          >
            Enviar veredicto
            <Icon type="far" size="sm" name="arrow-up-right" />
          </Button>
        )}
      </div>
      <VeredictCancelRequestModal
        isOpen={open}
        onClose={() => setOpen(false)}
        requestCode={formalityCode}
        cancellationId={latestCancellation.id}
        cancellationReason={latestCancellation.reason ?? ""}
      />
    </>
  );
};
